import { format } from 'date-fns';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { formatNumber } from '@/core';
import { Card, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Car } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Dollar } from '@/ui/icons/dollar';

type DashboardMetrics = {
  totalPayoutAmount?: number;
  ongoingRentalsCount?: number;
  upcomingRentalsCount?: number;
  totalCarsCount?: number;
  totalActiveCarsCount?: number;
};

export function getNextPaymentDateLabel(from = new Date()) {
  const year = from.getFullYear();
  const month = from.getMonth();
  const day = from.getDate();

  let nextDate = new Date(year, month + 1, 2);
  if (day <= 2) {
    nextDate = new Date(year, month, 2);
  } else if (day <= 16) {
    nextDate = new Date(year, month, 16);
  }

  return format(nextDate, 'MMM d');
}

type DashboardMetricsSectionProps = {
  metrics?: DashboardMetrics;
};

export default function DashboardMetricsSection({
  metrics,
}: DashboardMetricsSectionProps) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();

  return (
    <View style={{ gap: 16 }}>
      <Text type="title2">Metrics</Text>

      <View style={styles.metricItemsContainer}>
        <Pressable
          onPress={() => {
            router.replace('/partner/payouts');
          }}
          style={styles.metricItem}
        >
          <Card
            style={{
              padding: 16,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <Icon
                type="dressed"
                icon={<Dollar />}
                width={25}
                height={25}
                fillColor={theme.colors.success}
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: theme.colors.chip.success.backgroundColor,
                }}
              />

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 4,
                    alignItems: 'center',
                  }}
                >
                  <Text type="title1">
                    ${formatNumber(metrics?.totalPayoutAmount)}
                  </Text>
                </View>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Total Earnings
                </Text>
              </View>
            </View>

            <Icon
              type="naked"
              icon={<ChevronRight />}
              width={12}
              height={12}
              fillColor={theme.colors.icon.color}
            />
          </Card>
        </Pressable>

        <Pressable
          onPress={() => {
            router.replace('/partner/payouts');
          }}
          style={styles.metricItem}
        >
          <Card
            style={{
              padding: 16,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <Icon
                type="dressed"
                icon={<Dollar />}
                width={25}
                height={25}
                fillColor={theme.colors.warning}
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: theme.colors.chip.warning.backgroundColor,
                }}
              />

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 4,
                    alignItems: 'center',
                  }}
                >
                  <Text type="title1">{getNextPaymentDateLabel()}</Text>
                </View>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Upcoming Payment Date
                </Text>
              </View>
            </View>

            <Icon
              type="naked"
              icon={<ChevronRight />}
              width={12}
              height={12}
              fillColor={theme.colors.icon.color}
            />
          </Card>
        </Pressable>

        <Pressable
          onPress={() => {
            router.replace('/partner/reservations');
          }}
          style={styles.metricItem}
        >
          <Card
            style={{
              padding: 16,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <Icon
                type="dressed"
                icon={<CalendarIcon />}
                width={25}
                height={25}
                fillColor={theme.colors.success}
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: theme.colors.chip.success.backgroundColor,
                }}
              />

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 4,
                    alignItems: 'center',
                  }}
                >
                  <Text type="title1">{metrics?.ongoingRentalsCount}</Text>
                </View>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Ongoing rentals
                </Text>
              </View>
            </View>

            <Icon
              type="naked"
              icon={<ChevronRight />}
              width={12}
              height={12}
              fillColor={theme.colors.icon.color}
            />
          </Card>
        </Pressable>

        <Pressable
          onPress={() => {
            router.replace('/partner/reservations');
          }}
          style={styles.metricItem}
        >
          <Card
            style={{
              padding: 16,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 16,
                alignItems: 'center',
              }}
            >
              <Icon
                type="dressed"
                icon={<CalendarIcon />}
                width={25}
                height={25}
                fillColor={theme.colors.warning}
                style={{
                  width: 36,
                  height: 36,
                  backgroundColor: theme.colors.chip.warning.backgroundColor,
                }}
              />

              <View>
                <View
                  style={{
                    flexDirection: 'row',
                    gap: 4,
                    alignItems: 'center',
                  }}
                >
                  <Text type="title1">{metrics?.upcomingRentalsCount}</Text>
                </View>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  Upcoming rentals
                </Text>
              </View>
            </View>

            <Icon
              type="naked"
              icon={<ChevronRight />}
              width={12}
              height={12}
              fillColor={theme.colors.icon.color}
            />
          </Card>
        </Pressable>

        <View style={styles.carCardsContainer}>
          <Pressable
            style={styles.carCard}
            onPress={() => {
              router.replace('/partner/cars');
            }}
          >
            <Card
              style={{
                padding: 16,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  gap: 16,
                  alignItems: 'center',
                  flexShrink: 1,
                }}
              >
                <Icon
                  type="dressed"
                  icon={<Car />}
                  width={25}
                  height={25}
                  fillColor={theme.colors.icon.color}
                  style={{
                    width: 36,
                    height: 36,
                  }}
                />

                <View style={{ flexShrink: 1 }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                    }}
                  >
                    <Text type="title1">{metrics?.totalCarsCount}</Text>
                  </View>

                  <Text
                    type="caption1"
                    style={{
                      color: theme.colors.text.secondary,
                    }}
                  >
                    Total fleet
                  </Text>
                </View>
              </View>
            </Card>
          </Pressable>

          <Pressable
            style={styles.carCard}
            onPress={() => {
              router.replace('/partner/cars');
            }}
          >
            <Card
              style={{
                padding: 16,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  gap: 16,
                  alignItems: 'center',
                  flexShrink: 1,
                }}
              >
                <Icon
                  type="dressed"
                  icon={<Car />}
                  width={25}
                  height={25}
                  fillColor={theme.colors.icon.color}
                  style={{
                    width: 36,
                    height: 36,
                  }}
                />

                <View style={{ flexShrink: 1 }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                    }}
                  >
                    <Text type="title1">{metrics?.totalActiveCarsCount}</Text>
                  </View>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Listed cars
                  </Text>
                </View>
              </View>
            </Card>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet(() => ({
  metricItemsContainer: {
    flex: 1,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    flexWrap: {
      xs: 'nowrap',
      md: 'wrap',
    },
    gap: 16,
  },
  metricItem: {
    flex: {
      xs: 1,
      md: undefined,
    },
    minWidth: {
      md: 250,
    },
  },
  carCardsContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  carCard: {
    flex: {
      xs: 1,
      md: undefined,
    },
    minWidth: {
      md: 180,
    },
  },
}));
